import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { subscribeToDealExtractionStatus } from '../services/attachments-service';
import { loadDeal } from '../redux/actions/deal-actions';
import { fetchFiles } from '../redux/actions/deal-files-actions';
import { fetchCommercialProperty, fetchOrCreateVoucher } from '../redux/actions/voucher/voucher-actions';

const LEASE_EXTRACTION_REFRESH_EVENT = 'leaseExtractionPersistedDataRefreshed';
const LEASE_EXTRACTION_REFRESH_REQUEST_EVENT = 'leaseExtractionPersistedDataRefreshRequested';

const dispatchRefreshEvent = (detail) => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  const event = typeof CustomEvent === 'function'
    ? new CustomEvent(LEASE_EXTRACTION_REFRESH_EVENT, { detail })
    : new Event(LEASE_EXTRACTION_REFRESH_EVENT);
  window.dispatchEvent(event);
};

const dispatchLeaseExtractionRefreshRequest = ({
  dealId, fileId, refreshKey, extractionResult,
}) => {
  if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
  if (!extractionResult || extractionResult.status !== 'Success') return;

  const resolvedRefreshKey = refreshKey
    || `${fileId}:${extractionResult?.id || extractionResult?.status_text || extractionResult?.status}`;
  if (!dealId || !fileId || !resolvedRefreshKey) return;

  const detail = { dealId, fileId, refreshKey: resolvedRefreshKey };
  const event = typeof CustomEvent === 'function'
    ? new CustomEvent(LEASE_EXTRACTION_REFRESH_REQUEST_EVENT, { detail })
    : new Event(LEASE_EXTRACTION_REFRESH_REQUEST_EVENT);
  if (!event.detail) event.detail = detail;
  window.dispatchEvent(event);
};

const getStoredLeaseFile = (dealId, dealFiles) => {
  try {
    const stored = localStorage.getItem(`abstractionVoucherFile_${dealId}`);
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    if (!parsed || !parsed.id) return null;
    if (dealFiles.length > 0 && !dealFiles.some(dealFile => dealFile.id === parsed.id)) {
      localStorage.removeItem(`abstractionVoucherFile_${dealId}`);
      return null;
    }
    return parsed;
  } catch (error) {
    return null;
  }
};

const resolveActiveLeaseFile = ({ dealId, dealFiles, abstractionLeaseFile }) => {
  const files = dealFiles || [];
  const isLoadedFile = file => file && file.id && files.some(dealFile => dealFile.id === file.id);

  if (isLoadedFile(abstractionLeaseFile)) return abstractionLeaseFile;

  const storedFile = getStoredLeaseFile(dealId, files);
  if (storedFile) return storedFile;

  return files.find(file => file.is_lease_document === true)
    || files.find(file => file.extraction_opted_in === true)
    || files[0]
    || null;
};

const useLeaseExtractionPersistedDataRefresh = ({ deal, dealFiles, abstractionLeaseFile }) => {
  const dispatch = useDispatch();
  const refreshedExtractionRef = useRef(null);
  const fetchedFilesDealRef = useRef(null);
  const dealId = deal?.id;
  const dealType = deal?.deal_type;

  const activeFile = useMemo(() => {
    if (dealType !== 'lease' || !dealId) return null;
    return resolveActiveLeaseFile({
      dealId,
      dealFiles,
      abstractionLeaseFile,
    });
  }, [abstractionLeaseFile, dealFiles, dealId, dealType]);

  useEffect(() => {
    if (dealType !== 'lease' || !dealId) {
      fetchedFilesDealRef.current = null;
      return;
    }
    if (dealFiles && dealFiles.length > 0) return;
    if (fetchedFilesDealRef.current === dealId) return;
    fetchedFilesDealRef.current = dealId;
    dispatch(fetchFiles({
      deal: dealId,
      ordering: '-modified',
      page: 1,
      page_size: 25,
    }));
  }, [dealFiles, dealId, dealType, dispatch]);

  const refreshPersistedData = useCallback((refreshKey, fileId) => {
    if (!dealId || dealType !== 'lease' || !fileId) return;
    dispatch(loadDeal(dealId));
    dispatch(fetchOrCreateVoucher(dealId));
    dispatch(fetchCommercialProperty(dealId));
    dispatch(fetchFiles({
      deal: dealId,
      ordering: '-modified',
      page: 1,
      page_size: 25,
    }));
    dispatchRefreshEvent({ dealId, fileId, refreshKey });
  }, [dealId, dealType, dispatch]);

  const refreshPersistedDataOnce = useCallback((refreshKey, fileId) => {
    if (refreshedExtractionRef.current === refreshKey) return;
    refreshedExtractionRef.current = refreshKey;
    refreshPersistedData(refreshKey, fileId);
  }, [refreshPersistedData]);

  const handleExtractionResponse = useCallback((result, fileId) => {
    const status = result?.status || null;

    if (status !== 'Success') {
      if (status === 'Queued' || status === 'Processing' || status === 'Failure') {
        refreshedExtractionRef.current = null;
      }
      return;
    }

    const refreshKey = `${fileId}:${result?.id || result?.status_text || status}`;
    refreshPersistedDataOnce(refreshKey, fileId);
  }, [refreshPersistedDataOnce]);

  useEffect(() => {
    const handleRefreshRequest = (event) => {
      if (dealType !== 'lease' || !dealId) return;
      const detail = event?.detail || {};
      if (detail.dealId !== dealId || !detail.fileId || !detail.refreshKey) return;
      // Only the currently tracked active lease file should trigger a persisted
      // data refresh. Getting Started renders one VoucherFileListItem per file,
      // and every row broadcasts this event when ITS OWN extraction status is
      // 'Success'. Without this filter, every already-completed file (more
      // common as file count grows) fires its own voucher/commercial-property/
      // files refresh burst, even though nothing changed for this voucher.
      if (!activeFile || detail.fileId !== activeFile.id) return;
      refreshPersistedDataOnce(detail.refreshKey, detail.fileId);
    };

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      window.addEventListener(LEASE_EXTRACTION_REFRESH_REQUEST_EVENT, handleRefreshRequest);
    }

    return () => {
      if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
        window.removeEventListener(LEASE_EXTRACTION_REFRESH_REQUEST_EVENT, handleRefreshRequest);
      }
    };
  }, [activeFile, dealId, dealType, refreshPersistedDataOnce]);

  useEffect(() => {
    if (dealType !== 'lease' || !dealId || !activeFile || !activeFile.id) {
      return undefined;
    }

    const fileId = activeFile.id;
    const unsubscribe = subscribeToDealExtractionStatus({
      dealId,
      fileId,
      onUpdate: (result) => handleExtractionResponse(result, fileId),
      onError: () => {},
    });

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [activeFile, dealId, dealType, handleExtractionResponse]);
};

export { LEASE_EXTRACTION_REFRESH_EVENT, dispatchLeaseExtractionRefreshRequest };
export default useLeaseExtractionPersistedDataRefresh;
